import { storeToRefs } from "pinia";
import Pusher from "pusher-js";

import {
    mapEventFromServer,
    useSettingsStore,
    useNotificationsStore,
    useNotificationSoundStore,
    useNotificationSettingsStore,
    handlersByNotifyStatus,
} from "@/entities";
import { makeHttpRequest } from "@/shared";

let pusherInstance = null;

// Статус события колокольчика -> ключ персональной настройки пользователя.
const PERSONAL_SETTING_BY_STATUS = {
    OPENED: "systemNotifyShift",
    CLOSED: "systemNotifyShift",
    STARTED: "systemNotifyRent",
    FINISHED: "systemNotifyRent",
    OVERDUE: "systemNotifyRent",
    CANCELED: "systemNotifyRent",
    RENT_CLOSED: "systemNotifyRent",
    REPAIR_STATUS: "systemNotifyRent",
    ENDING_SOON: "notifyBeforeEndRent",
    TARIFF_EXPIRING: "systemNotifyTariff",
};

// Новое бронирование (CREATED) — виджет сайта или ВК, по источнику.
const resolveSettingKey = (data) => {
    if (data?.status === "CREATED") {
        return data?.serviceFrom === "vk"
            ? "systemNotifyVk"
            : "systemNotifyWidget";
    }

    return PERSONAL_SETTING_BY_STATUS[data?.status] ?? null;
};

export const initPusher = () => {
    const key = import.meta.env.VITE_PUSHER_KEY;
    const notify = useNotificationsStore();
    const sound = useNotificationSoundStore();
    const notificationSettings = useNotificationSettingsStore();
    const { settings } = storeToRefs(useSettingsStore());
    const { settings: personalSettings } = storeToRefs(notificationSettings);

    const groupId = settings.value?.groupId;

    // Без ключа или группы realtime не поднимаем — колокольчик и так работает через REST.
    if (!key || !groupId) return;

    // Канал общий на группу, поэтому персональные настройки колокольчика проверяем
    // на клиенте — иначе сотрудник получает живые события, которые сам выключил.
    if (!notificationSettings.loaded) {
        notificationSettings.load();
    }

    // Идемпотентность: на повторной авторизации не плодим соединения/подписки.
    if (pusherInstance) {
        pusherInstance.disconnect();
        pusherInstance = null;
    }

    const showNotification = (event) => {
        const handler = handlersByNotifyStatus[event.data?.status];

        if (!handler) return;

        // Персональный гейт: тип выключен у пользователя — не показываем (как и в списке).
        const settingKey = resolveSettingKey(event.data);

        if (settingKey && personalSettings.value?.[settingKey] === false) {
            return;
        }

        const item = handler(event.data, event.id);

        if (!item) return;

        // Тост показываем всегда; звук — только если включён (проверяется внутри play()).
        notify.setNotify(item);
        sound.play();
    };

    const pusher = new Pusher(key, {
        cluster: import.meta.env.VITE_PUSHER_CLUSTER,
        wsHost: import.meta.env.VITE_PUSHER_HOST || undefined,
        wsPort: Number(import.meta.env.VITE_PUSHER_PORT) || undefined,
        wssPort: Number(import.meta.env.VITE_PUSHER_PORT) || undefined,
        forceTLS: import.meta.env.VITE_PUSHER_TLS === "true",
        enabledTransports: ["ws", "wss"],
        channelAuthorization: {
            customHandler: ({ socketId, channelName }, callback) => {
                makeHttpRequest({
                    url: "/broadcasting/auth",
                    method: "POST",
                    data: { socket_id: socketId, channel_name: channelName },
                })
                    .then((response) => callback(null, response.data))
                    .catch((error) => callback(error, null));
            },
        },
    });

    pusherInstance = pusher;

    const channel = pusher.subscribe(`private-group.${groupId}`);

    channel.bind("human-shift-event", (data) => {
        showNotification(mapEventFromServer(data));
    });

    channel.bind("rent-event", (data) => {
        showNotification(mapEventFromServer(data));
    });

    channel.bind("tariff-event", (data) => {
        showNotification(mapEventFromServer(data));
    });

    // Входящий звонок: своя карточка с кнопками, мимо колокольчика —
    // поэтому без id, чтобы звонок не оседал в списке уведомлений
    channel.bind("my-calls-event", (event) => {
        const call = event?.data;

        if (!call?.number) return;

        notify.setNotify({
            type: "phone",
            icon: "phone",
            title: "Входящий звонок",
            incomingCall: {
                number: call.number,
                callStart: call.call_start ?? null,
            },
        });
        sound.play();
    });
};
